import cx from 'classnames'
import React, { useRef } from 'react'

import FormInput from './FormInput'

type Props = {
  /** Label */
  label?: string
  /** Description */
  description?: string
  /** Html For tag */
  htmlFor: string
  /** List of options that can be selected */
  options: string[]
  /** Current value */
  value: string | null
  /** True if the value is required */
  isRequired?: boolean
  /** On change function. */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  /** Externally defined className for the input wrapper. */
  wrapperClassName?: string
  /** Externally defined className */
  className?: string
  /** True if the component is in invalid state */
  isInvalid?: boolean
}

/** Renders a radio button group where all possible values are aligned horizontally.
 * The selected element has some highlight styling on it.
 */
const PrettyRadioButtonGroup: React.FC<Props> = ({
  label,
  description,
  htmlFor,
  isRequired,
  wrapperClassName,
  className,
  isInvalid,
  options,
  value,
  onChange,
}) => {
  const optionsRef = useRef<HTMLDivElement | null>(null)

  const renderOptions = () => {
    return (
      <div
        className={cx(
          'sm:text-md grid w-max auto-cols-fr grid-flow-col rounded-md border shadow-sm',
          className,
          {
            'border-red-500 ring-1 ring-red-500 focus:border-red-500':
              isInvalid,
            'border-border-normal focus:border-primary-500 focus:ring-primary-500':
              !isInvalid,
          },
        )}
        ref={optionsRef}
      >
        {options.map((option, index) => {
          const isChecked = value === option

          return (
            <div
              key={option}
              className={cx(
                'flex-1 cursor-pointer border-0 border-solid text-center',
                {
                  'bg-primary-500 text-white': isChecked,
                  'border-r border-gray-text': index < options.length - 1,
                  'rounded-l-md': index === 0,
                  'rounded-r-md': index === options.length - 1,
                },
              )}
            >
              <input
                id={option}
                type="radio"
                name={htmlFor}
                value={option}
                checked={isChecked}
                onChange={onChange}
                className="hidden"
              />
              <label htmlFor={option} className="block cursor-pointer p-2.5">
                {option}
              </label>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <FormInput
      label={label}
      description={description}
      htmlFor={htmlFor}
      isRequired={isRequired}
      className={wrapperClassName}
    >
      {renderOptions()}
    </FormInput>
  )
}

PrettyRadioButtonGroup.defaultProps = {
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange: () => {},
}

export default PrettyRadioButtonGroup
